import { useState, useEffect, useCallback } from "react";
import { useTenant } from "../../../hooks/useTenant";
import EvaluationService from "../../../services/evaluation_service";
import ActivityService from "../../../services/activity_service";

export const useKioskGamification = (member) => {
    const { idTenant } = useTenant();
    const [liveMember, setLiveMember] = useState(member);
    const [allEvaluations, setAllEvaluations] = useState([]);
    const [selectedEvalIndex, setSelectedEvalIndex] = useState(0);
    const [latestEvaluation, setLatestEvaluation] = useState(null);
    const [groupedTopics, setGroupedTopics] = useState({});
    const [gamifiedActivities, setGamifiedActivities] = useState([]);
    
    // Novas states para as conquistas gerais (ex: frequência)
    const [achievementsSettings, setAchievementsSettings] = useState([]);
    const [rankSettings, setRankSettings] = useState([]);
    const [awardedAchievementIds, setAwardedAchievementIds] = useState([]);
    const [awardedAchievements, setAwardedAchievements] = useState([]);
    
    // Ranking / Leaderboard
    const [rankingData, setRankingData] = useState(null);
    const [loadingRanking, setLoadingRanking] = useState(true);
    
    // Níveis globais de avaliação
    const [levels, setLevels] = useState([]);
    
    const [loadingEval, setLoadingEval] = useState(true);
    const [activeTab, setActiveTab] = useState('avaliacao');

    useEffect(() => {
        if (!idTenant || !member?.id) {
            setLoadingEval(false);
            return;
        }

        let isMounted = true;
        setLoadingEval(true);

        const fetchGamificationData = async () => {
            try {
                // Import resources
                const GamificationService = (await import("../../../services/gamification_service")).default;
                const { collection, getDocs, getDoc, doc } = await import("firebase/firestore");
                const { db } = await import("../../../services/firebase");
                const MemberService = (await import("../../../services/member_service")).default;
                const { COLLECTIONS } = await import("../../../services/collections");

                // Fire parallel promises
                const LevelService = (await import("../../../services/level_service")).default;
                const [evaluations, allActivities, updatedMember, gamificationSettings, awardedSnapshot, allLevels, rankingSnapshot] = await Promise.all([
                    EvaluationService.getAllByMember(idTenant, member.id),
                    ActivityService.getActivities(idTenant),
                    MemberService.getMemberById(idTenant, member.id),
                    GamificationService.getSettings(idTenant).catch(() => null),
                    getDocs(collection(db, "tenants", idTenant, "members", member.id, COLLECTIONS.AWARDED_ACHIEVEMENTS)).catch(() => ({ docs: [] })),
                    LevelService.getLevels(idTenant).catch(() => []),
                    // Busca o ranking consolidado (1 read)
                    getDoc(doc(db, "tenants", idTenant, "gamification", "ranking")).catch(() => null),
                ]);

                if (!isMounted) return;

                if (allLevels && allLevels.length > 0) {
                    setLevels(allLevels);
                }

                if (updatedMember) {
                    setLiveMember(updatedMember);
                }

                if (gamificationSettings?.achievements) {
                    setAchievementsSettings(gamificationSettings.achievements);
                }
                if (gamificationSettings?.levelRanks) {
                    setRankSettings(gamificationSettings.levelRanks);
                }

                if (awardedSnapshot?.docs) {
                    const awarded = awardedSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                    setAwardedAchievements(awarded);
                    setAwardedAchievementIds(awarded.map(d => d.idAchievement || d.id));
                }

                // Ranking
                if (rankingSnapshot && rankingSnapshot.exists && rankingSnapshot.exists()) {
                    setRankingData(rankingSnapshot.data());
                }
                setLoadingRanking(false);

                // Filtra a trilha de Insígnias
                const evActivities = allActivities.filter(a => a.evaluationSystem === true).sort((a, b) => (a.order || 0) - (b.order || 0));
                setGamifiedActivities(evActivities);

                if (evaluations && evaluations.length > 0) {
                    setAllEvaluations(evaluations);
                    setSelectedEvalIndex(0);
                    const evalData = evaluations[0];
                    setLatestEvaluation(evalData);

                    let grouped = {};
                    if (evalData.objectives && Array.isArray(evalData.objectives)) {
                        evalData.objectives.forEach(obj => {
                            const objName = obj.name || "Geral";
                            grouped[objName] = obj.topics || [];
                        });
                    }
                    
                    setGroupedTopics(grouped);
                }
            } catch (error) {
                console.error("Erro ao buscar avaliações e trilha do membro:", error);
            } finally {
                if (isMounted) setLoadingEval(false);
            }
        };

        fetchGamificationData();

        return () => {
            isMounted = false;
        };
    }, [idTenant, member]);

    // Navegação entre avaliações
    const navigateEvaluation = useCallback((direction) => {
        setSelectedEvalIndex(prev => {
            const newIdx = prev + direction;
            if (newIdx < 0 || newIdx >= allEvaluations.length) return prev;
            const evalData = allEvaluations[newIdx];
            setLatestEvaluation(evalData);
            const grouped = {};
            if (evalData.objectives && Array.isArray(evalData.objectives)) {
                evalData.objectives.forEach(obj => {
                    grouped[obj.name || "Geral"] = obj.topics || [];
                });
            }
            setGroupedTopics(grouped);
            return newIdx;
        });
    }, [allEvaluations]);

    const goToEvaluation = useCallback((index) => {
        if (index < 0 || index >= allEvaluations.length) return;
        setSelectedEvalIndex(index);
        const evalData = allEvaluations[index];
        setLatestEvaluation(evalData);
        const grouped = {};
        if (evalData.objectives && Array.isArray(evalData.objectives)) {
            evalData.objectives.forEach(obj => {
                grouped[obj.name || "Geral"] = obj.topics || [];
            });
        }
        setGroupedTopics(grouped);
    }, [allEvaluations]);

    return {
        liveMember,
        allEvaluations,
        selectedEvalIndex,
        latestEvaluation,
        groupedTopics,
        gamifiedActivities,
        achievementsSettings,
        rankSettings,
        awardedAchievementIds,
        awardedAchievements,
        loadingEval,
        activeTab,
        levels,
        rankingData,
        loadingRanking,
        actions: {
            setActiveTab,
            navigateEvaluation,
            goToEvaluation
        }
    };
};
